<template>
  <div class="select" ref="select" :class="{ expand: expand, disabled: disabled }" v-click-outside="hide">
    <a class="select-toggle small" :class="{ error: error }" @click.prevent="toggle" :tabindex="disabled ? -1 : 0">
      <div>
        <span class="select-title">{{ title }}</span>
        <span class="select-option" v-if="selection">{{ selection }}</span>
      </div>
      <i v-if="expand && open" class="select-arrow af af-chevron-up"></i>
      <i v-else class="select-arrow af af-chevron-down"></i>
    </a>
    <ul v-if="open" class="select-list">
      <li class="select-item" v-for="option in options" :key="option.id">
        <a class="select-item-link" @click.prevent="select(option)">{{ option.description }}</a>
      </li>
    </ul>
  </div>
</template>
<script>
import ClickOutside from '@/pixel/utils/ClickOutside';
import smoothReflow from '@/plugins/smoothReflow';

export default {
  components: {},

  mixins: [smoothReflow],

  props: {
    /**
     * The title to display to the left of the selected value.
     */
    title: {
      type: String,
    },
    /**
     * @model
     * The current selected value.
     */
    modelValue: {
      type: [Number, String],
    },
    /**
     * The list of options to select from.
     */
    options: {
      type: Array,
    },
    /**
     * Boolean to take up all available horizontal space.
     */
    expand: {
      type: Boolean,
    },
    /**
     * Boolean to enable / disable the input.
     */
    disabled: {
      type: Boolean,
    },
    /**
     * Boolean to show the error state.
     */
    error: {
      type: Boolean,
    },
  },

  data() {
    return {
      open: false,
      option: null,
    };
  },

  created() {
    this.option = this.modelValue;
  },

  mounted() {
    if (this.expand) {
      this.$smoothReflow({
        el: this.$refs.select,
        hideOverflow: true,
      });
    }
  },

  methods: {
    toggle() {
      if (this.disabled) {
        this.open = false;
      } else {
        this.open = !this.open;
      }
    },

    hide() {
      if (!this.expand) {
        this.open = false;
      }
    },

    select(option) {
      this.option = option.id;
      /**
       * Event emitted on input
       * @property {Number} option The option id selected.
       */
      this.$emit('update:modelValue', this.option);
      this.open = false;
    },
  },

  watch: {
    modelValue: {
      handler(option) {
        this.option = option;
      },
    },
  },

  computed: {
    selection() {
      const selections = this.options ? this.options.filter((option) => option.id === this.option) : [];
      return selections.length ? selections[0].description : '';
    },
  },

  directives: {
    ClickOutside,
  },
};
</script>
<style lang="postcss">
@import '@/pixel/style/variables';

.select {
  position: relative;
  width: 240px;
  font-size: var(--font-size-14);
  color: var(--font-color);

  .select-list {
    position: absolute;
    list-style-type: none;
    width: 240px;
    background-color: var(--white);
    z-index: 3;
    background-color: var(--white);
    box-shadow: 0 var(--box-shadow-blur) var(--box-shadow-spread) var(--shadow-color);
    border-radius: 4px;
    overflow: hidden;
    margin-top: 1px;
  }

  .select-item {
    border-bottom: solid 1px var(--white);
    background-color: var(--white);
  }

  .select-item-link {
    display: block;
    padding: 8px 12px;

    /* purgecss ignore */
    &:hover {
      background-color: var(--grey-ultra-light);
      color: var(--accent-color);
    }
  }

  .select-title {
    margin-right: var(--space-1);
  }

  .select-option {
    margin-right: var(--space-1);
  }

  .select-arrow {
    font-size: 0.8rem;
  }

  &.expand {
    width: 100%;

    .select-toggle.small:not(.error) {
      border-radius: 0;
      border-right: none;
      border-left: none;
      padding: 20px;

      /* purgecss ignore */
      &:hover {
        border-color: var(--border-color);
        box-shadow: none;
      }

      /* purgecss ignore */
      &:focus {
        border-color: var(--border-color);
        box-shadow: none;
      }
    }

    .select-item-link {
      padding-left: 20px;
      padding-right: 20px;
    }

    .select-list {
      position: static;
      width: auto;
      border-radius: 0;
      box-shadow: none;
    }
  }

  &.disabled {
    opacity: 0.5;

    .select-toggle {
      cursor: auto;
    }
  }
}
</style>
